import * as angular from 'angular';
import { Observable } from 'rxjs';
import { filter, shareReplay } from 'rxjs/operators';
import { isPresent, getOwnerUsername } from '@mode/shared/util-js';

import { ofType } from '@mode/legacy/util-domain';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { Actions } from '~/actions';
import { AppStore } from '~/types/main.types';

import { CalcEditorModalState } from '@mode/legacy/types-app-state';
import { DataViewResponseTypes, SwitchViewResponseTypes } from '@mode/shared/contract-common';
import { SwitchViewClientService, toSwitchAttr } from '@mode/shared/data-access-webapp';
import { ToastService } from '@mode/shared/util-toast';
import { DataViewFacade, SwitchAttrsActions } from '@mode/switch-views/contract';
import { makeFeatureAttrFromCalc, makeRequestAttrFromCalc } from '@mode/switch-views/domain';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';
import { filterIsExploring, onExploringStateChanged } from './calc-fields-service.pipes';

type adaptedAction<T> = Omit<T, 'type'> & { queryToken: string };
export type CreateSwitchViewAttrProps = adaptedAction<SwitchAttrsActions.CreateSwitchViewAttr>;
export type UpdateSwitchViewAttrProps = adaptedAction<SwitchAttrsActions.UpdateSwitchViewAttr>;
export type DeleteSwitchViewAttrProps = adaptedAction<SwitchAttrsActions.DeleteSwitchViewAttr>;

export interface CalcObservables {
  model$: Observable<FlamingoTypes.ModeModel>;
  modalState$: Observable<CalcEditorModalState>;
  modalStateWithCalc$: Observable<CalcEditorModalState>;
}

export class CalcFieldsService {
  static $inject = [
    '$window',
    '$rootScope',
    'RxStoreService',
    'actions$',
    'ToastService',
    'SwitchViewService',
    'DataViewFacade',
  ];

  public observables: CalcObservables;

  constructor(
    private $window: angular.IWindowService,
    private $root: angular.IRootScopeService,
    private store: AppStore,
    private actions$: Observable<Actions>,
    private alertService: ToastService,
    private switchViewService: SwitchViewClientService,
    private dataViewFacade: DataViewFacade
  ) {
    this.initialize();
  }

  createCalc(props: CreateSwitchViewAttrProps) {
    this.store.dispatch(this.makeCreateCalcAction(props));
  }

  updateCalc(props: UpdateSwitchViewAttrProps) {
    this.store.dispatch(this.makeUpdateCalcAction(props));
  }

  deleteCalc(props: DeleteSwitchViewAttrProps) {
    this.store.dispatch(this.makeDeleteCalcAction(props));
  }

  private makeCreateCalcAction(props: CreateSwitchViewAttrProps) {
    return SwitchAttrsActions.createSwitchViewAttr(props);
  }

  private makeUpdateCalcAction(props: UpdateSwitchViewAttrProps) {
    return SwitchAttrsActions.updateSwitchViewAttr(props);
  }

  private makeDeleteCalcAction(props: DeleteSwitchViewAttrProps) {
    return SwitchAttrsActions.deleteSwitchViewAttr(props);
  }

  private initialize() {
    const { store, actions$ } = this;

    this.makeObservables();

    onExploringStateChanged(actions$, store).subscribe((isExploring) => {
      this.$root.$applyAsync(() => {
        const action = isExploring ? SwitchAttrsActions.makeOriginalAttrs() : SwitchAttrsActions.restoreOriginalAttrs();
        store.dispatch(action);
      });
    });

    // Persistence helpers
    const withPersistence = <T>() => filterIsExploring<T>(false, store);
    const withoutPersistence = <T>() => filterIsExploring<T>(true, store);

    // Create Actions
    const create$ = actions$.pipe(ofType(SwitchAttrsActions.createSwitchViewAttr.type));

    /**
     * @description Create a calc without persistence. Optimistically dispatch success
     * so the new calc is added to the store
     */
    create$.pipe(withoutPersistence()).subscribe(({ token, attr }) => {
      this.$root.$applyAsync(() =>
        store.dispatch(
          SwitchAttrsActions.createSwitchViewAttrSuccess({
            attr: makeFeatureAttrFromCalc(attr, true),
            token,
          })
        )
      );
    });

    /**
     * @description Create a calc with persistence. Make an API call to save the calc
     */
    create$.pipe(withPersistence()).subscribe(({ token, attr }) => {
      const accountId = getOwnerUsername(this.$window.document);
      const payload = makeRequestAttrFromCalc(attr);

      this.switchViewService
        .createAttr(accountId, token, payload as DataViewResponseTypes.SwitchViewAttr.RequestAttr)
        .then((attr: SwitchViewResponseTypes.SwitchViewAttrResource) => {
          this.$root.$applyAsync(() =>
            store.dispatch(
              SwitchAttrsActions.createSwitchViewAttrSuccess({
                token,
                attr: toSwitchAttr(attr),
              })
            )
          );
        })
        .catch((error: Error) => {
          this.$root.$applyAsync(() =>
            store.dispatch(
              SwitchAttrsActions.createSwitchViewAttrFailure({
                error,
              })
            )
          );
        });
    });

    // Delete Actions
    const delete$ = actions$.pipe(ofType(SwitchAttrsActions.deleteSwitchViewAttr.type));

    /**
     * @description Delete a calc without persistence. Optimistically dispatch success
     * so the calc is removed from the store
     */
    delete$.pipe(withoutPersistence()).subscribe(({ token, queryToken, attr }) => {
      this.$root.$applyAsync(() =>
        store.dispatch(
          SwitchAttrsActions.deleteSwitchViewAttrSuccess({
            queryToken,
            token,
            attr,
          })
        )
      );
    });

    /**
     * @description Delete a calc with persistence. Make an API call to remove the calc
     */
    delete$.pipe(withPersistence()).subscribe(({ token, queryToken, attr }) => {
      const accountId = getOwnerUsername(this.$window.document);
      const switchViewToken = token;

      this.switchViewService
        .deleteAttr(accountId, attr.token, switchViewToken)
        .then(() => {
          this.$root.$applyAsync(() =>
            store.dispatch(
              SwitchAttrsActions.deleteSwitchViewAttrSuccess({
                queryToken,
                token,
                attr,
              })
            )
          );
        })
        .catch((error: Error) => {
          this.$root.$applyAsync(() =>
            store.dispatch(
              SwitchAttrsActions.deleteSwitchViewAttrFailure({
                error,
              })
            )
          );
        });
    });

    // Update Actions
    const update$ = actions$.pipe(ofType(SwitchAttrsActions.updateSwitchViewAttr.type));

    /**
     * @description Update a calc without persistence. Optimistically dispatch success
     * so the calc is updated in the store
     */
    update$.pipe(withoutPersistence()).subscribe(({ token, queryToken, attr }) => {
      this.$root.$applyAsync(() =>
        store.dispatch(
          SwitchAttrsActions.updateSwitchViewAttrSuccess({
            attr: makeFeatureAttrFromCalc(attr),
            queryToken,
            token,
          })
        )
      );
    });

    /**
     * @description Update a calc with persistence. Make an API call to update the calc
     */
    update$.pipe(withPersistence()).subscribe(({ token, queryToken, attr }) => {
      const accountId = getOwnerUsername(this.$window.document);
      const payload = makeRequestAttrFromCalc(attr);
      const switchViewToken = token;

      this.switchViewService
        .updateAttr(accountId, switchViewToken, attr.token, payload as DataViewResponseTypes.SwitchViewAttr.RequestAttr)
        .then((attr: SwitchViewResponseTypes.SwitchViewAttrResource) => {
          const attrFeature = toSwitchAttr(attr);
          this.$root.$applyAsync(() =>
            store.dispatch(
              SwitchAttrsActions.updateSwitchViewAttrSuccess({
                queryToken,
                token,
                attr: attrFeature,
              })
            )
          );
        })
        .catch((error: Error) => {
          this.$root.$applyAsync(() =>
            store.dispatch(
              SwitchAttrsActions.updateSwitchViewAttrFailure({
                error,
              })
            )
          );
        });
    });
  }

  private makeObservables() {
    const model$ = this.dataViewFacade.model$.pipe(filter(isPresent), shareReplay(1));
    const modalState$ = this.store.selectNoEmpty(SelectedReportSelectors.getCalcEditorModal).pipe(shareReplay(1));
    const modalStateWithCalc$ = modalState$.pipe(filter(({ calc }) => calc != null));

    this.observables = {
      model$,
      modalState$,
      modalStateWithCalc$,
    };
  }
}
